import React from 'react';
import { useTranslation } from 'react-i18next';
import { Formik, FormikActions } from 'formik';
import { useSnackbarContext } from '@ui-lib/components/Snackbar/SnackbarProvider';
import ModalWindow from '@ui-lib/components/ModalWindow';
import useManualRequest from '@web-shared/hooks/useManualRequest';
import HttpError from '@web-shared/services/http-service/HttpError';
import { useBolAuthToken } from '../../../BolDetails/components/DeliveryTokenContext';
import { updateEbol } from '../../../../../../api';
import { EbolDetails } from '../../../../../../api/types/EbolDetails';
import { ebolActionSuccessMessages, initialValues, validationSchema } from './const';
import { EBOL_ACTION_REASON_SOURCE, EbolActionFormValues, EbolActionProps } from './types';
import EbolActionModalContent from './components/EbolActionModalContent';

type Props = EbolActionProps & {
  open: boolean;
  onClose: () => void;
  status: EBOL_ACTION_REASON_SOURCE;
};

const EbolActionModal = ({ open, onClose, ebolDetails, status, onStatusChange }: Props) => {
  const { t } = useTranslation();
  const { showErrorSnackbar, showSuccessSnackbar } = useSnackbarContext();
  const { trigger } = useManualRequest(updateEbol);
  const token = useBolAuthToken();

  const submitAction = async (values: EbolActionFormValues, { setSubmitting, setErrors }: FormikActions<EbolActionFormValues>) => {
    try {
      const details = await trigger({
        id: ebolDetails.id,
        status,
        token: token!,
        reasons: values.reasons.map(reason => reason.value),
        details: values.details,
      });
      showSuccessSnackbar({ message: t(ebolActionSuccessMessages[status]) });
      
      const updatedDetails: EbolDetails = {
        ...details,
        photos: [...ebolDetails.photos, ...values.photos],
      };
      
      onStatusChange(updatedDetails);
      setSubmitting(false);
      onClose();
    } catch (err) {
      setSubmitting(false);
      if (HttpError.isHttpError(err)) {
        showErrorSnackbar({ message: err.message });
        if (err.errors) {
          setErrors(err.errors as Record<string, string>);
        }
      }
    }
  };

  return (
    <ModalWindow
      open={open}
      onClose={onClose}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={submitAction}
      >
        <EbolActionModalContent
          onClose={onClose}
          status={status}
          ebolDetails={ebolDetails} />
      </Formik>
    </ModalWindow>
  );
};

export default EbolActionModal;